import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Badge, Chip, Center, Spinner, Metric, KV, PageHeader } from "@/components/ui";
import { Topbar } from "@/components/Layout";
import { api } from "@/lib/api";

const TABS = [["deposits", "Deposits & hours"], ["entries", "Ledger entries"], ["referrals", "Referrals"]];

export default function Ledger() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("deposits");
  const [msg, setMsg] = useState(null);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(null), 3500); };
  const load = useCallback(async () => { setLoading(true); try { setData(await api.get("/office/ledger")); } catch (e) { flash(e.message || "Failed to load"); } finally { setLoading(false); } }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="screen"><Topbar title="Ledger" /><Center><Spinner /></Center></div>;
  const s = data.summary;
  const tone = (st) => st === "paid" ? "success" : st === "forfeited" ? "error" : st === "refunded" ? "brand" : "warning";

  return (
    <div className="screen" data-testid="ledger-screen">
      <Topbar title="Deposits, free hours & referrals" subtitle={`Deposit: $${(s.deposit_amount_cents / 100).toFixed(0)} refundable`} />
      <div className="container">
        <PageHeader title="Deposits, free hours & referrals" onBack={() => nav("/office")} />
        {msg ? <div className="toast">{msg}</div> : null}
        <div className="metric-grid"><Metric label="Deposits paid" value={s.deposits_paid} testID="m-paid" /><Metric label="Forfeited" value={s.deposits_forfeited} testID="m-forfeited" /><Metric label="Refunded" value={s.deposits_refunded} /></div>
        <div className="metric-grid"><Metric label="Trial hrs outstanding" value={s.trial_hours_outstanding} /><Metric label="Referral hrs granted" value={s.referral_hours_granted} /><Metric label="Hours forfeited" value={s.hours_forfeited} /></div>
        <div className="chip-wrap" style={{ margin: "12px 0" }}>{TABS.map(([k, l]) => <Chip key={k} testID={`ledger-tab-${k}`} label={l} selected={tab === k} onClick={() => setTab(k)} />)}</div>

        {tab === "deposits" && (data.deposits || []).map((d) => (
          <Card key={d.client_id} testID={`dep-${d.client_id}`}>
            <div className="between"><b>{d.client_name}</b><Badge label={`deposit: ${d.status}`} tone={tone(d.status)} /></div>
            <KV k="trial hours" v={`${d.trial_hours} hrs${d.forfeited ? " (forfeited)" : ""}`} mono />
            <KV k="referral hours" v={`${d.referral_hours} hrs`} mono />
            <KV k="booking" v={d.booking_start ? `${new Date(d.booking_start).toLocaleString()} · ${d.booking_status}` : "—"} mono />
            {d.paid_at ? <KV k="paid" v={new Date(d.paid_at).toLocaleString()} mono /> : null}
            {data.can_write && ["paid", "forfeited"].includes(d.status) ? <Button small testID={`refund-${d.client_id}`} variant="ghost" icon="rotate-ccw" label="Refund deposit (Stripe)" onClick={async () => { try { await api.post(`/office/deposits/${d.client_id}/refund`); flash("Refund issued."); load(); } catch (e) { flash(e.message || "Refund failed"); } }} /> : null}
          </Card>
        ))}

        {tab === "entries" && ((data.entries || []).length === 0 ? <p className="note">No ledger entries yet.</p> : data.entries.map((e) => (
          <div key={e.id} className="row-item" data-testid={`entry-${e.id}`}>
            <div className="between"><b>{e.client_name}</b><span style={{ fontFamily: "var(--mono)", fontWeight: 800, color: e.hours < 0 ? "var(--error)" : e.hours > 0 ? "var(--success)" : "var(--muted)" }}>{e.hours > 0 ? "+" : ""}{e.hours} hrs</span></div>
            <div className="row-sub">{e.type.replace(/_/g, " ")} · {e.by} · {new Date(e.at).toLocaleString()}</div>
            <div style={{ fontSize: 13 }}>{e.note}</div>
          </div>
        )))}

        {tab === "referrals" && ((data.referrals || []).length === 0 ? <p className="note">No referrals yet.</p> : data.referrals.map((r) => (
          <div key={r.id} className="row-item" data-testid={`ref-${r.id}`}><b>{r.referrer_name || "?"} → {r.referred_name || "?"}</b><div className="row-sub">{r.hours_awarded} hrs granted to referrer · referred family gets 8 free hrs · {new Date(r.created_at).toLocaleDateString()}</div></div>
        )))}
      </div>
    </div>
  );
}
